import React, { useEffect, useRef, useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { ValidateOtpForExternalUserAsync } from '../../../services/ExternalUsersService/ExternalUsersService';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Loader from '../loader/Loader';
import { useAuth } from '../../../firebase/AuthProvider';
import { LobbyState } from '../../../interfaces/videocallDtos/videoCall';
import * as signalR from "@microsoft/signalr";
import { ReceiveGroupCallInviteResponse } from '../../../interfaces/videoHubResponse/videoHubResponse';
import { Button, Spinner } from 'react-bootstrap';
import { HubConnection } from '@microsoft/signalr';
import { LOBBY_TIMEOUT_MINUTES } from '../../../Data/SD';

const ExternalUserOtpPage: React.FC = () => {
    const { logout } = useAuth();
    const length = 6;
    const [otp, setOtp] = useState<string[]>(Array(length).fill(''));
    const inputsRef = useRef<(HTMLInputElement | null)[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [waitingLoading, setWaitingLoading] = useState<boolean>(false);
    const connectionRef = useRef<HubConnection | null>(null);
    const { ticketId } = useParams<{ ticketId: string }>();

    const [lobbyTimedOut, setLobbyTimedOut] = useState(false);
    const lobbyTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const [waitingSeconds, setWaitingSeconds] = useState(0);

    // prevent body scroll while waiting
    useEffect(() => {
        document.body.style.overflow = waitingLoading ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [waitingLoading]);
    
    // cleanup connection on unmount
    useEffect(() => {
        return () => {
            if (connectionRef.current) {
                connectionRef.current.stop().catch(() => { /* ignore */ });
                connectionRef.current = null;
            }
        };
    }, []);

    const navigate = useNavigate();

    useEffect(() => {
        logout();
    }, []);

    const handleChange = (value: string, index: number) => {
        if (!/^\d?$/.test(value)) return;

        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);

        if (value && index < length - 1) {
            inputsRef.current[index + 1]?.focus();
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
        if (e.key === 'Backspace') {
            if (otp[index]) {
                const newOtp = [...otp];
                newOtp[index] = '';
                setOtp(newOtp);
            } else if (index > 0) {
                inputsRef.current[index - 1]?.focus();
            }
        }
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLFormElement>) => {
        e.preventDefault();
        const pasteData = e.clipboardData.getData('Text').replace(/\D/g, '').slice(0, length);
        if (!pasteData) return;

        const newOtp = [...otp];
        for (let i = 0; i < length; i++) {
            newOtp[i] = pasteData[i] || '';
        }
        setOtp(newOtp);

        const nextIndex = newOtp.findIndex((digit) => digit === '');
        if (nextIndex !== -1) {
            inputsRef.current[nextIndex]?.focus();
        } else {
            inputsRef.current[length - 1]?.blur();
        }
    };

    const handleSubmit = () => {
        const finalOtp = otp.join('');
        if (finalOtp.length === length && ticketId) {
            setLoading(true);
            ValidateOtpForExternalUserAsync(ticketId, finalOtp).then((res) => {
                if (res.success) {
                    localStorage.setItem("ExternalUserDetails", JSON.stringify(res.data));
                    toast.success("Otp validated Successfully");

                    const state: LobbyState = {
                        // calenderEventId : res.data.calendarEventId,
                        isPublisher: false,
                        isOneToOneCall: false,
                    }

                    if (res.data.roomId != null) {
                        navigate(`/external/lobby/${res.data.roomId}`, { state: state })
                    } else {
                        // Use API base URL for hub and tempSessionToken as access token
                        const tokenforHub = '';
                        const hubBase = (process.env.REACT_APP_API_BASE_URL ?? window.location.origin).replace(/\/$/, '');
                        const hubURL = `${hubBase}/videoHub?ticketId=${res.data.ticketId}&userId=${res.data.externalUserDetailId}`;

                        const connection = new signalR.HubConnectionBuilder()
                            .withUrl(hubURL, { accessTokenFactory: async () => tokenforHub })
                            .withAutomaticReconnect()
                            .build();

                        // keep reference so we can stop it from the Cancel button
                        connectionRef.current = connection;

                        connection.onclose((err) => {
                            if (err) console.error("SignalR connection closed with error:", err);
                            else console.log("SignalR connection closed.");
                            setWaitingLoading(false);
                           connectionRef.current = null;
                        });

                        connection.on("ReceiveExternalGroupCallInvite", (data : ReceiveGroupCallInviteResponse) => {
                            console.log("Received external group invite:", data);
                            setWaitingLoading(false);
                            navigate(`/external/lobby/${data.roomId}`, { state: state });
                        });

                        connection.start()
                            .then(() => {
                                console.log("SignalR connected in external user");
                                return connection.invoke("JoinExternalInviteGroup", res.data.calendarEventId);
                            })
                            .then(() => {
                                console.log("Joined external invite group");
                                // show waiting UI until ReceiveExternalGroupCallInvite arrives
                                setWaitingLoading(true);
                            })
                            .catch((err) => {
                                console.error("SignalR start/invoke error:", err);
                                setWaitingLoading(false);
                                toast.error("Real-time connection failed.");
                               connectionRef.current = null;
                            });
                    }

                    // redirect to the external User lobby
                } else {
                    toast.error(res.message);
                }
            }).catch(err => {
                toast.error(err.message);
            }).finally(() => {
                setLoading(false);
            })
        }
    };

    // Cancel / leave waiting and stop the SignalR connection
    const cancelWaiting = async () => {
        setWaitingLoading(false);
        if (connectionRef.current) {
            try {
                await connectionRef.current.stop();
            } catch (err) {
                console.error("Error stopping connection:", err);
            }
            connectionRef.current = null;
        }
    };

    // Start/clear timeout when waitingLoading changes
    useEffect(() => {
        if (waitingLoading) {
            setLobbyTimedOut(false);
            lobbyTimeoutRef.current = setTimeout(() => {
                setLobbyTimedOut(true);
                setWaitingLoading(false);
                toast.error("Lobby timed out. Please try joining again.");
                // Optionally, notify backend about timeout here
            }, LOBBY_TIMEOUT_MINUTES * 60 * 1000);
        } else {
            if (lobbyTimeoutRef.current) {
                clearTimeout(lobbyTimeoutRef.current);
                lobbyTimeoutRef.current = null;
            }
        }
        return () => {
            if (lobbyTimeoutRef.current) {
                clearTimeout(lobbyTimeoutRef.current);
                lobbyTimeoutRef.current = null;
            }
        };
    }, [waitingLoading]);

    // Timer for waiting duration
    useEffect(() => {
        let interval: NodeJS.Timeout | null = null;
        if (waitingLoading) {
            setWaitingSeconds(0);
            interval = setInterval(() => {
                setWaitingSeconds((prev) => prev + 1);
            }, 1000);
        } else {
            setWaitingSeconds(0);
            if (interval) clearInterval(interval);
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [waitingLoading]);

    // Helper to format seconds as mm:ss
    const formatTime = (secs: number) => {
        const m = Math.floor(secs / 60).toString().padStart(2, '0');
        const s = (secs % 60).toString().padStart(2, '0');
        return `${m}:${s}`;
    };

    return (
        <div className="container mt-5">
            {loading && <Loader />}
             {waitingLoading && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'rgba(0,0,0,0.6)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 2000,
                        pointerEvents: 'auto'
                    }}
                    aria-live="polite"
                    aria-busy="true"
                >
                    <Spinner animation="border" variant="light" role="status" style={{ width: '3rem', height: '3rem' }}>
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                    <p style={{ color: '#fff', marginTop: '1rem', fontSize: '1.1rem' }}>
                        Waiting for the meeting to start...
                    </p>
                    <div style={{ color: '#fff', marginTop: '0.5rem', fontSize: '1.2rem' }}>
                        {formatTime(waitingSeconds)}
                    </div>
                    <Button
                        variant='danger'
                        onClick={cancelWaiting}
                        style={{ marginTop: '1rem' }}
                    >
                        Cancel
                    </Button>
                </div>
            )}
            {lobbyTimedOut && (
                <div className="alert alert-warning text-center mt-4">
                    Lobby timed out. Please try joining again.
                    <div>
                        <Button variant="primary" onClick={() => {
                            setLobbyTimedOut(false);
                            setOtp(Array(length).fill(''));
                            // Optionally, trigger re-join logic here
                        }}>
                            Retry
                        </Button>
                    </div>
                </div>
            )}
            <h3 className="text-center mb-4">Enter the 6-digit OTP</h3>
            <form
                className="d-flex justify-content-center gap-2"
                onPaste={handlePaste}
                onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmit();
                }}
            >
                {otp.map((digit, index) => (
                    <input
                        key={index}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        className="form-control text-center"
                        style={{ width: '3rem', height: '3rem', fontSize: '1.5rem' }}
                        value={digit}
                        onChange={(e) => handleChange(e.target.value, index)}
                        onKeyDown={(e) => handleKeyDown(e, index)}
                        ref={(el) => (inputsRef.current[index] = el)}
                        aria-label={`Digit ${index + 1}`}
                    />
                ))}
            </form>

            <div className="text-center mt-4">
                <button
                    type="button"
                    className="btn btn-primary"
                    disabled={otp.includes('')}
                    onClick={handleSubmit}
                >
                    Verify OTP
                </button>
            </div>
        </div>
    );
};

export default ExternalUserOtpPage;
